import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Group, GroupMemberWithProfile } from '@ext-bookmark/shared/types';
import * as groupService from '../../services/group/groupService';

const GROUPS_STALE_TIME_MS = 2 * 60 * 1000; // 2 minutes
const GROUPS_GC_TIME_MS = 5 * 60 * 1000;   // 5 minutes

const commonGroupQueryOptions = {
  staleTime: GROUPS_STALE_TIME_MS,
  gcTime: GROUPS_GC_TIME_MS,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
};

export type UseGroupsQueryOptions = { enabled?: boolean };

/**
 * Fetch current user's groups via React Query.
 * Query key: ['groups'].
 */
export function useGroupsQuery(options?: UseGroupsQueryOptions) {
  return useQuery({
    queryKey: ['groups'],
    queryFn: async (): Promise<Group[]> => {
      const { groups, error } = await groupService.getUserGroups(undefined, { noCache: true });
      if (error) throw new Error(error.message);
      return groups;
    },
    ...commonGroupQueryOptions,
    ...(options && { enabled: options.enabled }),
  });
}

/**
 * Fetch members for a given group. Query key: ['groups', groupId, 'members'].
 * Enabled only when groupId is present.
 */
export function useGroupMembersQuery(groupId: string | null | undefined) {
  return useQuery({
    queryKey: ['groups', groupId, 'members'],
    queryFn: async (): Promise<GroupMemberWithProfile[]> => {
      if (!groupId) return [];
      const { members, error } = await groupService.getGroupMembers(groupId, undefined, { noCache: true });
      if (error) throw new Error(error.message);
      return members;
    },
    enabled: !!groupId,
    ...commonGroupQueryOptions,
  });
}

// --- Mutations ---

/**
 * Create a group. On success invalidates ['groups'].
 */
export function useCreateGroupMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { group, error } = await groupService.createGroup(name);
      if (error) throw new Error(error.message);
      return group!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export type UpdateGroupPayload = { groupId: string; name?: string; color?: string | null };

/**
 * Update group name and/or color. On success invalidates ['groups'] and ['groups', groupId, 'members'].
 */
export function useUpdateGroupMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateGroupPayload) => {
      const { groupId, name, color } = payload;
      if (name !== undefined) {
        const { error } = await groupService.updateGroupName(groupId, name);
        if (error) throw new Error(error.message);
      }
      if (color !== undefined) {
        const { error } = await groupService.updateGroupColor(groupId, color);
        if (error) throw new Error(error.message);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['groups', variables.groupId, 'members'] });
    },
  });
}

/**
 * Accept an invitation. On success invalidates ['groups'].
 */
export function useAcceptInvitationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { member, error } = await groupService.acceptInvitation(invitationId);
      if (error) throw new Error(error.message);
      return member;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

/**
 * Reject an invitation. On success invalidates ['groups'].
 */
export function useRejectInvitationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await groupService.rejectInvitation(invitationId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}
